import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import styled from 'styled-components';
import _ from 'lodash';
import { AgGridReact } from 'ag-grid-react';
import {
  ColDef,
  GridApi,
  GridOptions,
  IServerSideDatasource,
  RowClassParams,
} from 'ag-grid-community';

import { useIsDataTable } from 'components/DataTable';
import BaseViewsContext from 'contexts/BaseViewsContext';
import DatasetDefinitionsContext from 'contexts/DatasetDefinitionsContext';
import getFieldType from 'utils/getFieldType';
import StyleOverrides, {
  GRID_BORDERS_CSS,
  GRID_DARK_HEADER_CSS,
} from 'components/Grid/StyleOverrides';
import getBaseViewFields from 'components/Grid/getBaseViewFields';
import GridFrameworkComponents from 'components/Grid/GridFrameworkComponents';
import buildGridOptions from 'components/Grid/buildGridOptions';
import PerformanceBoardSlideOutContext from 'components/PerformanceBoards/contexts/PerformanceBoardSlideOutContext';
import useTotalsRow from 'components/PerformanceBoards/hooks/useTotalsRow';
import useAggregatedBonusDatasource from 'components/PerformanceBoards/hooks/useAggregatedBonusDatasource';
import useEntityField from 'components/TargetManager/hooks/useEntityField';
import useDatasource from 'hooks/grids/useDatasource';
import { getCellRenderer, getType } from 'hooks/useColumnDefs';
import isDefined from 'isDefined';
import PerformanceBoardSlideOut from '../../PerformanceBoardSlideOut';
import useGetFieldLabel from 'hooks/useGetFieldLabel';
import { getSlideOutWidth } from 'components/PerformanceBoards/PerformanceBoardSlideOut/SlideOut';
import EntityDefinitionsContext from '../../../../contexts/EntityDefinitionsContext';
import { FleetOpsCellRendererParams } from '../../../../types/agGrid';
import { RowHeightContext } from '../../../../contextProviders/SplashScreenProviders/MiscProviders/RowHeightProvider';
import { PERFORMANCE_STATUS_CELL } from '../../constants';
import { StatusCellProps } from './PerformanceStatusCell';
import useFilterInput from '../../../../hooks/useFilterInput';
import useAutoSizeStrategy from '../../../../hooks/useAgGridAutoSizeStrategy';
import PerformanceBoardDataTableView from './PerformanceBoardDataTableView';

const Wrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: row;
  position: relative;
  overflow: hidden;
`;

// Both squish and overlay narrow the grid pane by the panel width when the
// slideout is open; they differ only in whether the columns are refit into the
// narrower pane (squish) or kept at full width so they overflow into a
// horizontal scroll (overlay — see `suppressAutoSizeForOverlay` below).
const Left = styled.div<{
  isSlideOutOpen?: boolean;
  slideOutWidth: number;
}>`
  height: 100%;
  width: ${(props) =>
    props.isSlideOutOpen ? `calc(100% - ${props.slideOutWidth}px)` : '100%'};

  ${GRID_BORDERS_CSS}
  ${GRID_DARK_HEADER_CSS}
`;

const useColDefs = (
  board: PerformanceBoardTypes.Board,
  baseView: FleetOps.BaseView,
  sortOverride: SimpleGridSort,
) => {
  const { datasets } = useContext(DatasetDefinitionsContext);
  const { getEntityAndDefaultAppForField } = useContext(
    EntityDefinitionsContext,
  );
  const entityField = useEntityField(board.dataType);
  const { getFieldLabel } = useGetFieldLabel();

  const toColDef = useCallback(
    (colOrderItem: PerformanceBoardTypes.Column): ColDef | undefined => {
      const fieldView = getBaseViewFields(baseView).find(
        (f) => f.field === colOrderItem.field,
      );
      if (!fieldView) {
        return undefined;
      }

      const pinnedEntry =
        board.columnSizeStrategy?.type === 'fitCellContents'
          ? board.columnSizeStrategy.pinnedColumns?.find(
              (p) => p.field === colOrderItem.field,
            )
          : undefined;
      const pinnedLeft = pinnedEntry ? ('left' as const) : undefined;
      const pinnedWidth = pinnedEntry?.width;

      if (colOrderItem.type === 'progress') {
        const cell: BoardProgressCell = {
          type: 'Progress',
          totalField: colOrderItem.field,
          targetField: `${colOrderItem.field}Target`,
          differenceField: `${colOrderItem.field}Difference`,
          headerName: `${getFieldLabel({
            field: colOrderItem.field,
            dataType: board.dataType,
          })} Progress`,
          tooltipTitle: `${getFieldLabel({
            field: colOrderItem.field,
            dataType: board.dataType,
          })} Progress`,
        };

        return {
          field: cell.headerName,
          type: 'numericColumn',
          cellRenderer: 'progressCell',
          cellRendererParams: {
            cell,
            fieldView,
          },
          pinned: pinnedLeft,
          width: pinnedWidth,
        };
      }

      if (colOrderItem.type === 'status') {
        const statusCell: StatusCellProps = {
          totalField: colOrderItem.field,
          targetField: `${colOrderItem.field}Target`,
          differenceField: `${colOrderItem.field}Difference`,
          dataset: baseView.type,
        };

        const cellRendererParams = {
          cell: statusCell,
        };

        return {
          field: getFieldLabel({
            field: colOrderItem.field,
            dataType: board.dataType,
          }),
          type: 'numericColumn',
          cellRenderer: PERFORMANCE_STATUS_CELL,
          cellRendererParams,
          pinned: pinnedLeft,
          width: pinnedWidth,
        };
      }

      const entityDefinition = getEntityAndDefaultAppForField({
        field: fieldView.field,
      });
      const { cellRenderer, isLeftAlign } = getCellRenderer({
        isLaneCell: false,
        isCustomerLaneBoard: false,
        isPerformanceBoard: true,
        entityField,
        board,
        fieldView,
        isEntityField: !!entityDefinition,
        getEntityAndDefaultAppForField,
      });
      const fieldType = getFieldType({
        datasets,
        datasetType: fieldView.dataset,
        fieldName: fieldView.field,
      });
      const cellRendererParams: FleetOpsCellRendererParams = {
        fieldView,
        fieldType,
        entityDefinition,
        isLeftAlign,
      };

      return {
        field: colOrderItem.field,
        cellRenderer,
        cellRendererParams,
        initialSort:
          colOrderItem.field === sortOverride.field
            ? sortOverride.direction
            : undefined,
        headerName: fieldView.nameAlias
          ? fieldView.nameAlias
          : _.startCase(fieldView.field),
        type: getType(fieldType),
        menuTabs: [],

        rowGroup: false,
        enableRowGroup: true,
        editable: fieldView.field === 'reasonCode',
        cellEditor:
          fieldView.field === 'reasonCode' ? 'reasonCodePicker' : undefined,
        cellEditorPopup: fieldView.field === 'reasonCode',
        headerClass: isLeftAlign ? undefined : 'rightAlignedHeader',
        pinned: pinnedLeft,
        width: pinnedWidth,
      };
    },
    [
      baseView,
      board,
      datasets,
      entityField,
      getEntityAndDefaultAppForField,
      getFieldLabel,
      sortOverride.direction,
      sortOverride.field,
    ],
  );

  const buildColDefs = useCallback(() => {
    return board.columnOrder
      .filter((c) => c.visible)
      .map(toColDef)
      .filter(isDefined);
  }, [board.columnOrder, toColDef]);
  const [colDefs, setColDefs] = useState<ColDef[]>(() => buildColDefs());
  useEffect(() => {
    setColDefs(buildColDefs());
  }, [buildColDefs]);

  return colDefs;
};

// The selected entity's row (SelectedEntityProvider sets the context), on
// both renderers
const ROW_CLASS_RULES = {
  'performance-board-row-selected': (params: RowClassParams) =>
    !!params.context?.selectedEntity &&
    params.data?.[params.context.entityField] === params.context.selectedEntity,
};
const DATA_TABLE_GRID_OPTIONS = { rowClassRules: ROW_CLASS_RULES };

const useGridOptions = (
  dataSource: IServerSideDatasource | DataSource | undefined,
  board: PerformanceBoardTypes.Board,
  gridApi: GridApi | undefined,
  suppressAutoSizeForOverlay: boolean,
) => {
  const { rowHeight } = useContext(RowHeightContext);
  const autoSizeStrategy = useAutoSizeStrategy(board.columnSizeStrategy);
  const buildNewGridOptions = useCallback(() => {
    if (!dataSource) {
      return undefined;
    }
    const gridOptions: GridOptions = {
      ...buildGridOptions({ rowHeight }),
      rowModelType: 'serverSide',
      popupParent: document.querySelector('body'),
      autoSizeStrategy,
      suppressColumnVirtualisation: true,
      rowClassRules: ROW_CLASS_RULES,
    };

    return gridOptions;
  }, [autoSizeStrategy, dataSource, rowHeight]);
  const [gridOptions, setGridOptions] = useState<GridOptions | undefined>(() =>
    buildNewGridOptions(),
  );
  useEffect(() => {
    setGridOptions(buildNewGridOptions());
  }, [buildNewGridOptions]);

  const resizeGrid = useCallback(() => {
    if (!gridApi || !autoSizeStrategy || gridApi.isDestroyed()) {
      return;
    }

    // Overlay + open: leave the columns at their full width so they overflow the
    // narrowed pane into a horizontal scroll beside the panel, instead of being
    // refit/compressed. On close this flag is false again, so the normal refit
    // runs and the overflow (and its scrollbar) disappears.
    if (suppressAutoSizeForOverlay) {
      return;
    }

    if (autoSizeStrategy.type === 'fitGridWidth') {
      gridApi.sizeColumnsToFit({
        defaultMinWidth: autoSizeStrategy.defaultMinWidth,
        columnLimits: autoSizeStrategy.columnLimits?.map((l) => ({
          ...l,
          key: l.colId,
        })),
      });
    } else if (autoSizeStrategy.type === 'fitCellContents') {
      gridApi.autoSizeAllColumns();
    }
  }, [autoSizeStrategy, gridApi, suppressAutoSizeForOverlay]);

  const debouncedResizeGrid = useMemo(
    () => _.debounce(resizeGrid, 250),
    [resizeGrid],
  );

  useEffect(() => {
    if (!gridApi || gridApi.isDestroyed()) {
      return;
    }
    gridApi.addEventListener('gridSizeChanged', debouncedResizeGrid);
    return () => {
      if (gridApi.isDestroyed()) {
        return;
      }

      gridApi.removeEventListener('gridSizeChanged', debouncedResizeGrid);
    };
  }, [gridApi, debouncedResizeGrid]);

  return gridOptions;
};

// AG's server-side grid, over search2 or the aggregated sign-off rows
const AgPerformanceBoardGrid = ({
  board,
  isAggregated,
  baseView,
  colDefs,
  filters,
  totalsRow,
  gridApi,
  setGridApi,
  suppressAutoSizeForOverlay,
}: {
  board: PerformanceBoardTypes.Board;
  isAggregated: boolean;
  baseView: FleetOps.BaseView;
  colDefs: ColDef[];
  filters: FilterInput[];
  totalsRow: any;
  gridApi: GridApi | undefined;
  setGridApi: React.Dispatch<React.SetStateAction<GridApi | undefined>>;
  suppressAutoSizeForOverlay: boolean;
}) => {
  // Sign-off boards of sub-period programmes aggregate by driver over the parent
  // period; every other board keeps the raw search2 datasource untouched. Decided
  // by the parent (useShouldAggregateSignOff) so the export button agrees.

  const { dataSource: searchDataSource } = useDatasource(
    undefined,
    baseView.type,
    filters,
  );
  const { dataSource: aggregatedDataSource } = useAggregatedBonusDatasource({
    board,
    filters,
  });
  const dataSource = isAggregated ? aggregatedDataSource : searchDataSource;

  const gridOptions = useGridOptions(
    dataSource,
    board,
    gridApi,
    suppressAutoSizeForOverlay,
  );

  if (!gridOptions) {
    return null;
  }

  return (
    <StyleOverrides
      style={{
        height: '100%',
        width: '100%',
      }}
    >
      <div
        style={{
          height: '100%',
          width: '100%',
          fontSize: '11px',
        }}
        className="ag-theme-balham ag-theme-fleetops"
      >
        <AgGridReact
          serverSideDatasource={dataSource}
          gridOptions={gridOptions}
          columnDefs={colDefs}
          components={GridFrameworkComponents}
          pinnedBottomRowData={totalsRow}
          onGridReady={(event) => {
            setGridApi(event.api);
          }}
        />
      </div>
    </StyleOverrides>
  );
};

const PerformanceBoardGrid = ({
  board,
  isAggregated,
  baseView,
  sortOverride,
  totalsRow,
  gridApi,
  setGridApi,
  setExportHandle,
}: {
  board: PerformanceBoardTypes.Board;
  isAggregated: boolean;
  baseView: FleetOps.BaseView;
  sortOverride: SimpleGridSort;
  totalsRow: any;
  gridApi: GridApi | undefined;
  setGridApi: React.Dispatch<React.SetStateAction<GridApi | undefined>>;
  setExportHandle: (handle: DataTable.ExportHandle | undefined) => void;
}) => {
  const isDataTable = useIsDataTable('performanceBoard');
  const colDefs = useColDefs(board, baseView, sortOverride);
  const { filterInput } = useFilterInput({});

  const filters = useMemo((): FilterInput[] => {
    return [filterInput];
  }, [filterInput]);

  const { isOpen: isSlideOutOpen } = useContext(
    PerformanceBoardSlideOutContext,
  );

  const slideOutWidth = getSlideOutWidth(board);
  const isOverlay = board.slideoutDisplayMode === 'overlay';
  // Overlay keeps columns full-width (they overflow the narrowed pane into a
  // horizontal scroll); squish lets the grid refit them into the smaller pane.
  const suppressAutoSizeForOverlay = isOverlay && isSlideOutOpen;

  return (
    <Wrapper>
      <Left isSlideOutOpen={isSlideOutOpen} slideOutWidth={slideOutWidth}>
        {isDataTable ? (
          <PerformanceBoardDataTableView
            board={board}
            isAggregated={isAggregated}
            dataType={baseView.type}
            colDefs={colDefs}
            filters={filters}
            sortOverride={sortOverride}
            gridOptions={DATA_TABLE_GRID_OPTIONS}
            totalsRow={totalsRow}
            isRefitPaused={suppressAutoSizeForOverlay}
            setGridApi={setGridApi}
            onExportHandle={setExportHandle}
          />
        ) : (
          <AgPerformanceBoardGrid
            board={board}
            isAggregated={isAggregated}
            baseView={baseView}
            colDefs={colDefs}
            filters={filters}
            totalsRow={totalsRow}
            gridApi={gridApi}
            setGridApi={setGridApi}
            suppressAutoSizeForOverlay={suppressAutoSizeForOverlay}
          />
        )}
      </Left>
      <PerformanceBoardSlideOut />
    </Wrapper>
  );
};

const Gate = ({
  board,
  isAggregated,
  sortOverride,
  gridApi,
  setGridApi,
  setExportHandle,
}: {
  board: PerformanceBoardTypes.Board;
  isAggregated: boolean;
  sortOverride: SimpleGridSort;
  gridApi: GridApi | undefined;
  setGridApi: React.Dispatch<React.SetStateAction<GridApi | undefined>>;
  setExportHandle: (handle: DataTable.ExportHandle | undefined) => void;
}) => {
  const { baseViews } = useContext(BaseViewsContext);
  const bv = baseViews[board.dataType];
  const totalsRow = useTotalsRow({ baseView: bv });

  if (!bv) {
    return null;
  }

  return (
    <PerformanceBoardGrid
      board={board}
      isAggregated={isAggregated}
      baseView={bv}
      sortOverride={sortOverride}
      totalsRow={totalsRow}
      gridApi={gridApi}
      setGridApi={setGridApi}
      setExportHandle={setExportHandle}
    />
  );
};

export default Gate;
